import { useQuery } from '@tanstack/react-query'
import { ClipboardList, AlertTriangle, Activity, TrendingUp } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState } from 'react'

interface AuditLog {
  id: string
  tenant_id: string | null
  user_id: string | null
  action: string
  category: string
  method: string
  path: string
  status_code: number | null
  resource_type: string | null
  resource_id: string | null
  ip_address: string | null
  user_agent: string | null
  duration_ms: number | null
  created_at: string
  user_name: string | null
  user_email: string | null
  tenant_name: string | null
}

interface AuditStats {
  today: number
  thisWeek: number
  errorsThisWeek: number
  topActions: Array<{ action: string; count: number }>
}

interface AuditFiltersData {
  actions: string[]
  categories: string[]
  users: Array<{ user_id: string; name: string; email: string }>
  tenants: Array<{ tenant_id: string; name: string }>
}

const methodColors: Record<string, { bg: string; text: string }> = {
  GET: { bg: 'var(--color-brand-primary-50)', text: 'var(--color-brand-primary)' },
  POST: { bg: '#dcfce7', text: '#166534' },
  PUT: { bg: '#fef3c7', text: '#92400e' },
  DELETE: { bg: '#fee2e2', text: '#991b1b' },
}

const categoryLabels: Record<string, string> = {
  access: 'Acceso',
  action: 'Accion',
  admin: 'Admin',
  read: 'Lectura',
  system: 'Sistema',
}

function StatusCodeBadge({ code }: { code: number | null }) {
  if (!code) return <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>—</span>
  const isError = code >= 400
  return (
    <span
      className="inline-flex rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums"
      style={{
        backgroundColor: isError ? '#fee2e2' : '#dcfce7',
        color: isError ? '#991b1b' : '#166534',
      }}
    >
      {code}
    </span>
  )
}

export function AdminAuditPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<Record<string, string>>({})
  const perPage = 30

  const updateFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({})
    setPage(1)
  }

  const hasActiveFilters = Object.values(filters).some((v) => v !== '' && v !== undefined)

  // Fetch audit stats
  const { data: stats } = useQuery({
    queryKey: ['admin', 'audit', 'stats'],
    queryFn: () => api.get<AuditStats>('/admin/audit/stats'),
    staleTime: 30 * 1000,
  })

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ['admin', 'audit', 'filters'],
    queryFn: () => api.get<AuditFiltersData>('/admin/audit/filters'),
    staleTime: 60 * 1000,
  })

  // Fetch audit logs
  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'audit', 'logs', page, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: String(perPage) })
      for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v)
      }
      return apiFetchJson<{
        ok: boolean
        data: AuditLog[]
        meta: { page: number; perPage: number; total: number; totalPages: number }
      }>(`/admin/audit?${params}`)
    },
  })

  const logs = response?.data ?? []
  const meta = response?.meta

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auditoria"
        description="Registro de accesos y acciones realizadas en la plataforma. Historico de 1 ano."
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
                <Activity className="h-5 w-5" style={{ color: 'var(--color-brand-primary)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{stats.today.toLocaleString('es-ES')}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Eventos hoy</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: '#dbeafe' }}>
                <TrendingUp className="h-5 w-5" style={{ color: '#2563eb' }} />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{stats.thisWeek.toLocaleString('es-ES')}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Ultimos 7 dias</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: '#fee2e2' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#dc2626' }} />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{stats.errorsThisWeek.toLocaleString('es-ES')}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Errores (7d)</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: '#f3e8ff' }}>
                <ClipboardList className="h-5 w-5" style={{ color: '#7c3aed' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Top accion</p>
                <p className="text-xs font-mono" style={{ color: 'var(--color-text-tertiary)' }}>
                  {stats.topActions[0]?.action ?? '—'} ({stats.topActions[0]?.count ?? 0})
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Search */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Buscar</label>
          <input
            type="text"
            placeholder="Accion, ruta, usuario..."
            value={filters.search ?? ''}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)', minWidth: '220px' }}
          />
        </div>

        {/* Category */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Categoria</label>
          <select
            value={filters.category ?? ''}
            onChange={(e) => updateFilter('category', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todas</option>
            {(filterOptions?.categories ?? []).map((cat) => (
              <option key={cat} value={cat}>{categoryLabels[cat] ?? cat}</option>
            ))}
          </select>
        </div>

        {/* Action */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Accion</label>
          <select
            value={filters.action ?? ''}
            onChange={(e) => updateFilter('action', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todas</option>
            {(filterOptions?.actions ?? []).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        {/* Method */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Metodo</label>
          <select
            value={filters.method ?? ''}
            onChange={(e) => updateFilter('method', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todos</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Estado</label>
          <select
            value={filters.statusCode ?? ''}
            onChange={(e) => updateFilter('statusCode', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todos</option>
            <option value="error">Solo errores (4xx/5xx)</option>
            <option value="200">200 OK</option>
            <option value="201">201 Created</option>
            <option value="400">400 Bad Request</option>
            <option value="401">401 Unauthorized</option>
            <option value="403">403 Forbidden</option>
            <option value="404">404 Not Found</option>
            <option value="500">500 Internal Error</option>
          </select>
        </div>

        {/* User */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Usuario</label>
          <select
            value={filters.userId ?? ''}
            onChange={(e) => updateFilter('userId', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todos</option>
            {(filterOptions?.users ?? []).map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>
            ))}
          </select>
        </div>

        {/* Tenant */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Cliente</label>
          <select
            value={filters.tenantId ?? ''}
            onChange={(e) => updateFilter('tenantId', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <option value="">Todos</option>
            {(filterOptions?.tenants ?? []).map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Date from */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Desde</label>
          <input
            type="date"
            value={filters.dateFrom ?? ''}
            onChange={(e) => updateFilter('dateFrom', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </div>

        {/* Date to */}
        <div>
          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Hasta</label>
          <input
            type="date"
            value={filters.dateTo ?? ''}
            onChange={(e) => updateFilter('dateTo', e.target.value)}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </div>

        {/* Clear */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {/* Logs Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                {['Fecha', 'Usuario', 'Cliente', 'Accion', 'Metodo', 'Ruta', 'Estado', 'Duracion', 'IP'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-4">
                      <div className="h-8 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
                    </td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                    Sin registros de auditoria
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                    {/* Fecha */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatDateTime(log.created_at)}
                      </span>
                    </td>

                    {/* Usuario */}
                    <td className="px-4 py-2.5">
                      {log.user_name ? (
                        <div>
                          <p className="text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>{log.user_name}</p>
                          <p className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>{log.user_email}</p>
                        </div>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sistema</span>
                      )}
                    </td>

                    {/* Cliente */}
                    <td className="px-4 py-2.5">
                      <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        {log.tenant_name ?? '—'}
                      </span>
                    </td>

                    {/* Accion */}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                          style={{
                            backgroundColor: 'var(--color-gray-100)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {categoryLabels[log.category] ?? log.category}
                        </span>
                        <span className="text-xs font-mono" style={{ color: 'var(--color-text-primary)' }}>
                          {log.action}
                        </span>
                      </div>
                    </td>

                    {/* Metodo */}
                    <td className="px-4 py-2.5">
                      <span
                        className="inline-flex rounded px-1.5 py-0.5 text-xs font-bold"
                        style={{
                          backgroundColor: methodColors[log.method]?.bg ?? 'var(--color-gray-100)',
                          color: methodColors[log.method]?.text ?? 'var(--color-text-secondary)',
                        }}
                      >
                        {log.method}
                      </span>
                    </td>

                    {/* Ruta */}
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                        {log.path}
                      </span>
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-2.5">
                      <StatusCodeBadge code={log.status_code} />
                    </td>

                    {/* Duracion */}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                        {log.duration_ms != null ? `${log.duration_ms}ms` : '—'}
                      </span>
                    </td>

                    {/* IP */}
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-mono" style={{ color: 'var(--color-text-tertiary)' }}>
                        {log.ip_address ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination — always visible */}
          {!isLoading && (
            <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                {meta?.total?.toLocaleString('es-ES') ?? 0} registros
              </p>
              <div className="flex gap-1">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                  {page} / {meta?.totalPages ?? 1}
                </span>
                <Button variant="secondary" size="sm" disabled={page >= (meta?.totalPages ?? 1)} onClick={() => setPage(page + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
